import React, { useEffect, useState } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import FLFPairingTypeControlPanel from "../../components/ControlPanel/FLFPairingTypeControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import PairingTypeTable from "../../components/Table/PairingTypeTable";
import {
  createFLFPairingType,
  getFLFPairingType,
  removeFLFPairingType,
  updateFLFPairingType,
} from "src/apis/FLF/pairingType.api";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import { CACHE_PAIRING_TYPE } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import { dataStateType } from "src/constants/status";

const initPairingTypeData = {
  list: [],
  total_record: 0,
};

const title = "FLF Pairing Type";

const FLFPairingType = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [pairingTypeData, setPairingTypeData] = useState(initPairingTypeData);
  const [filterPairingTypes, setFilterPairingTypes] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const permissionsAccessControl = useGetPermissionAccessControl();
  const [errorsForm, setErrorsForm] = useState({});

  const fetchPairingTypes = useCallback(() => {
    getFLFPairingType({}, (result) => {
      setPairingTypeData({ ...result });
      setFilterPairingTypes([...result.list]);
    });
  }, []);

  useEffect(() => {
    fetchPairingTypes();
  }, [fetchPairingTypes]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredPairingType = useCallback((pairingTypes) => {
    setFilterPairingTypes(pairingTypes);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (pairingTypes, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return pairingTypes.filter((pairingType) => {
        return pairingType.name
          .toLowerCase()
          .includes(key.toLowerCase().trim());
      });
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredPairingTypes = value
        ? searchByKeyWord(pairingTypeData.list, value)
        : pairingTypeData.list;
      updateFilteredPairingType(filteredPairingTypes);
    },
    [pairingTypeData.list, searchByKeyWord, updateFilteredPairingType]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */

  const addOrEdit = useCallback(
    (pairingType, resetForm) => {
      if (pairingType.id) {
        updateFLFPairingType(pairingType.id, pairingType, handleApiResponse);
      } else {
        createFLFPairingType(pairingType, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchPairingTypes();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_PAIRING_TYPE);
        }
      }
    },
    [fetchPairingTypes, setCurrentActionCache]
  );

  /* ---- handle delete ----- */
  const handleDelete = useCallback(
    (pairingTypeDeleted) => {
      NotifyConfirm("warning", "Are you delete it ?", (result) => {
        if (result.isConfirmed) {
          removeFLFPairingType(
            pairingTypeDeleted.id,
            setCurrentActionCache(CACHE_PAIRING_TYPE)
          );
          setPairingTypeData((prev) => ({
            ...prev,
            list: prev.list.filter(
              (pairingType) => pairingType.id !== pairingTypeDeleted.id
            ),
            total_record: prev.total_record - 1,
          }));

          updateFilteredPairingType((prev) =>
            prev.filter(
              (pairingType) => pairingType.id !== pairingTypeDeleted.id
            )
          );
        }
      });
    },
    [setCurrentActionCache, updateFilteredPairingType]
  );

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterPairingTypes, searchKey),
    [filterPairingTypes, searchByKeyWord, searchKey]
  );
  const listPairingTypeRender = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{title}</h2>
      </div>

      <FLFPairingTypeControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={true}
        />
      </FLFPairingTypeControlPanel>

      <PairingTypeTable
        permissionsAccessControl={permissionsAccessControl}
        data={listPairingTypeRender}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={status}
      />

      <Pagination
        pathname={path.flfPairingType}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFPairingType;
